import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, MessageSquare, ArrowUpRight, HelpCircle } from "lucide-react";

const FAQS = [
  {
    section: "Membership",
    items: [
      { q: "Who can join?", a: "US residents over 18 who can verify their identity by Veriff and provide a legitimate, verifiable source for the gift cards they intend to sell. We do not accept resellers, anonymous-trade volume, or unverifiable supply chains." },
      { q: "Is there a fee to apply or stay a member?", a: "No. Membership is free. Our revenue comes from the spread between the wholesale rate we negotiate and the rate we pass back to members." },
      { q: "How long does the application process take?", a: "Most applications are reviewed within two business days. After approval you'll complete a 2-minute KYC by Veriff, then capture a bank account. Typical end-to-end: 48 hours." },
      { q: "Can I leave anytime?", a: "Yes. Reach out to support and we'll deactivate your account. Pending payouts complete as scheduled, and bank data is purged 90 days after final payout." },
    ],
  },
  {
    section: "Deals & rates",
    items: [
      { q: "Why are the rates higher than what I see elsewhere?", a: "We pool member supply to negotiate larger lots. A single seller can't credibly offer the volume that a buying group can, so we capture rates closer to the wholesale price." },
      { q: "How are rates set?", a: "We negotiate per-deal with our buyers and publish the rate alongside a face-value range, total cap, and per-member cap. The rate is locked when you reserve." },
      { q: "What's the difference between a reservation and a submission?", a: "A reservation locks your share of the deal at the published rate. You then submit cards against that reservation by the deal's deadline. Unfilled portions are released back to the pool when the deal closes." },
    ],
  },
  {
    section: "Cards & validation",
    items: [
      { q: "What cards do you accept?", a: "Each deal specifies a brand, face-value range, and validation rules. The most common rules are 16-digit card number, 4-digit PIN, and a face value within the range. Some brands have brand-specific prefix or barcode rules." },
      { q: "What if a card is flagged?", a: "Flagged cards stay in the queue and our admins triage them. We may reach out by email asking for clarification (e.g., a photo of the back of the card). You can also fix the line and resubmit." },
      { q: "What if a card is rejected?", a: "Rejected cards are excluded from your payout but otherwise have no penalty. Your reservation balance remains available for replacement cards until the deadline." },
    ],
  },
  {
    section: "Payments & security",
    items: [
      { q: "How do I get paid?", a: "By ACH to the bank account on file. Each deal publishes its payment terms (e.g., Net 7 days from approval). Payouts arrive in batches; you can track every status from your payment history." },
      { q: "How is my bank data stored?", a: "Account numbers are encrypted with AWS KMS before being written to disk. Only Payments Overseers can decrypt them, and every decryption is logged with the admin's ID, IP, and timestamp." },
      { q: "What about taxes?", a: "We file 1099-NEC for members with $600+ in earnings in a calendar year. You'll receive your form by January 31 the following year." },
    ],
  },
];

// PUB-05 — FAQ
export default function FAQPage() {
  const [openId, setOpenId] = useState("Membership-0");
  const [activeSection, setActiveSection] = useState("Membership");

  return (
    <div className="bg-surface">
      {/* Hero */}
      <section className="relative overflow-hidden bg-hero-rich border-b border-line">
        <div className="absolute inset-0 bg-grid-faint pointer-events-none opacity-50" />
        <div className="relative max-w-site mx-auto px-6 lg:px-12 pt-16 pb-14 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-surface/70 backdrop-blur-sm border border-line text-caption text-brand-700 uppercase tracking-wider font-semibold mb-6">
              <HelpCircle className="size-3.5" /> Frequently asked
            </div>
            <h1 className="font-brand font-bold text-ink leading-[1.04] tracking-[-0.025em] text-[44px] sm:text-[60px] lg:text-[68px]">
              Common questions,
              <br />
              <span className="relative inline-block text-brand-700">
                straight answers.
                <svg className="absolute -bottom-2 left-0 w-full" height="14" viewBox="0 0 300 14" fill="none" preserveAspectRatio="none">
                  <path d="M2 8 Q 80 2, 150 7 T 298 6" stroke="#5EEAD4" strokeWidth="3" strokeLinecap="round" fill="none" />
                </svg>
              </span>
            </h1>
            <p className="mt-7 text-lg text-ink-muted leading-relaxed max-w-xl">
              If something here isn&rsquo;t clear, write to us at{" "}
              <a href="mailto:support@gcbc.com" className="text-brand-700 font-semibold underline-offset-4 hover:underline">support@gcbc.com</a>
              {" "}or use{" "}
              <Link to="/contact" className="text-brand-700 font-semibold underline-offset-4 hover:underline">the contact form</Link>.
              We respond within one business day.
            </p>
          </div>

          {/* Right-side decorative stat card — fills the empty hero space.
              Pure decoration: surfaces the depth of the FAQ + invites a scan. */}
          <div className="hidden lg:block lg:col-span-5">
            <div className="relative bg-surface border border-line rounded-3xl p-7 shadow-xl shadow-ink/[0.06]">
              <div className="flex items-center justify-between mb-5">
                <span className="eyebrow text-brand-700">By the numbers</span>
                <span className="size-9 rounded-full bg-brand-50 text-brand-700 grid place-items-center"><HelpCircle className="size-4" /></span>
              </div>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-5">
                <div>
                  <dt className="text-caption text-ink-subtle">Questions answered</dt>
                  <dd className="mt-1 font-brand text-[34px] font-bold text-ink leading-none tabular-nums">{FAQS.reduce((s, c) => s + c.items.length, 0)}</dd>
                </div>
                <div>
                  <dt className="text-caption text-ink-subtle">Topic areas</dt>
                  <dd className="mt-1 font-brand text-[34px] font-bold text-ink leading-none tabular-nums">{FAQS.length}</dd>
                </div>
                <div>
                  <dt className="text-caption text-ink-subtle">Avg reply time</dt>
                  <dd className="mt-1 font-brand text-[34px] font-bold text-brand-700 leading-none">&lt;1<span className="text-h2 text-ink-subtle">d</span></dd>
                </div>
                <div>
                  <dt className="text-caption text-ink-subtle">Members helped</dt>
                  <dd className="mt-1 font-brand text-[34px] font-bold text-ink leading-none tabular-nums">2,400+</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky section nav + accordions */}
      <section className="max-w-site mx-auto px-6 lg:px-12 py-16 grid lg:grid-cols-12 gap-12">
        {/* Sticky section TOC */}
        <aside className="lg:col-span-4 lg:sticky lg:top-24 lg:self-start">
          <div className="eyebrow mb-4 text-brand-700">Browse by</div>
          <nav className="space-y-1">
            {FAQS.map((s) => (
              <button
                key={s.section}
                onClick={() => {
                  setActiveSection(s.section);
                  document.getElementById(`section-${s.section}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-md text-left transition-all ${
                  activeSection === s.section ? "bg-brand-50 text-brand-800 font-semibold" : "text-ink-muted hover:bg-surface-alt hover:text-ink"
                }`}
              >
                <span className="font-brand text-body font-semibold tracking-tight">{s.section}</span>
                <span className="text-caption text-ink-hint tabular-nums">{s.items.length}</span>
              </button>
            ))}
          </nav>
        </aside>

        {/* Accordions */}
        <div className="lg:col-span-8 space-y-14">
          {FAQS.map((s) => (
            <div key={s.section} id={`section-${s.section}`} className="scroll-mt-24">
              <div className="flex items-baseline gap-4 mb-6 pb-4 border-b border-line">
                <span className="font-mono text-caption text-brand-700 tabular-nums">
                  0{FAQS.indexOf(s) + 1}
                </span>
                <h2 className="font-brand text-h2 text-ink font-bold tracking-[-0.015em]">{s.section}</h2>
              </div>
              <ul className="border-y border-line divide-y divide-line/60">
                {s.items.map((item, i) => {
                  const id = `${s.section}-${i}`;
                  const open = openId === id;
                  return (
                    <li key={i}>
                      <button onClick={() => setOpenId(open ? null : id)}
                              className="w-full text-left py-5 flex items-start justify-between gap-4 group transition-colors"
                              data-testid={`faq-q-${i}`}>
                        <span className={`text-body font-brand font-semibold tracking-tight transition-colors ${open ? "text-brand-700" : "text-ink group-hover:text-brand-700"}`}>
                          {item.q}
                        </span>
                        <ChevronDown className={`size-5 shrink-0 mt-1 transition-all ${open ? "text-brand-700 rotate-180" : "text-ink-hint group-hover:text-ink"}`} />
                      </button>
                      {open && (
                        <div className="pb-6 pr-9 text-body text-ink-muted leading-relaxed">{item.a}</div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="border-t border-line bg-surface-alt">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 py-14 flex flex-wrap items-center justify-between gap-6">
          <div>
            <div className="eyebrow text-brand-700 mb-2">Still curious?</div>
            <h3 className="font-brand text-h1 text-ink font-bold tracking-[-0.02em]">Talk to a human.</h3>
            <p className="mt-2 text-body-sm text-ink-muted">Drop us a note and we&rsquo;ll respond within one business day.</p>
          </div>
          <Link to="/contact"
                className="group relative inline-flex items-center gap-3 h-12 pl-6 pr-2 rounded-full bg-ink hover:bg-brand-700 text-white font-semibold text-body-sm transition-all duration-300 shadow-[0_4px_14px_rgba(15,23,42,0.18)] hover:shadow-[0_6px_20px_rgba(15,118,110,0.32)]">
            <MessageSquare className="size-4" /> <span>Contact us</span>
            <span className="size-8 rounded-full bg-white/15 grid place-items-center transition-transform group-hover:rotate-45">
              <ArrowUpRight className="size-3.5" strokeWidth={2.4} />
            </span>
          </Link>
        </div>
      </section>
    </div>
  );
}
